import type {Banner, OrderCountData, PerformanceData, WarehouseData} from './types';
import type {HeaderDataPayload} from '@ds/types';

import {BellOutlined, BulbOutlined} from '@ant-design/icons';
import {Divider} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {getBranches} from 'store/slices/dashboard/thunk';
import {getBanners, getOrderCount, getPerformance, getWarehouse} from 'store/slices/home/thunk';
import {pollNotification} from 'store/slices/notification/thunk';
import {getOrdersReportNew} from 'store/slices/order/thunk';
import {getVendorGrade} from 'store/slices/profile/thunk';
import {isEmptyObject, numberFormat} from 'utils/Helper';
import {sendDataToLighthouse} from 'utils/sendDataToLighthouse';

import BannerCarousel from './components/BannerCarousel/BannerCarousel';
import DashboardChart from './components/DashboardChart/DashboardChart';
import HomeSkeleton from './components/HomeSkeleton/HomeSkeleton';
// import PromotionPopup from './components/PromotionPopup/PromotionPopup';
import StatCard from './components/StatCard/StatCard';
import VendorBadge from './components/VendorBadge/VendorBadge';
import WarehouseSection from './components/WarehouseSection/WarehouseSection';
import ActionRow from './components/common/ActionRow/ActionRow';
import SectionHeader from './components/common/SectionHeader/SectionHeader';

type HomeProps = {
  headerData?: (data: HeaderDataPayload) => void;
};

const Home: React.FC<HomeProps> = ({headerData}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {selectedVendor, profileId} = useAppSelector((state) => state.dashboard);
  const {
    home: {gettingHomeData, performance, orderCount, warehouse, banners},
    order: {orderReports},
    profile: {vendorGrade}
  } = useAppSelector((state) => state);
  // const [showPromotionPopup, setShowPromotionPopup] = useState(false);
  const selectedVendorId = selectedVendor?.id;

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

    if (profileId && selectedVendorId) {
      sendDataToLighthouse(
        {
          data: JSON.stringify({display_mode: isStandalone ? 'standalone' : 'browser'}),
          action_name: 'displayMode'
        },
        userAgent
      );
    }
  }, [profileId, selectedVendorId]);

  useEffect(() => {
    if (!selectedVendorId) {
      dispatch(getBranches({page: 0, pageSize: 10}));
      return;
    }

    const vendorId = selectedVendorId;
    dispatch(pollNotification({vendor_id: vendorId}))
      .unwrap()
      .then((response) => {
        headerData?.({
          icon: <BellOutlined aria-hidden />,
          action: () => navigate('/notification-center'),
          badgeNumber: response.data.notification_count,
          iconButtonSize: 'lg'
        });
      })
      .catch(() => undefined);

    const today = new Date().toISOString().split('T')[0];
    dispatch(getOrdersReportNew({vid: vendorId, start_date: today, end_date: today}));
    dispatch(getOrderCount({vendor_id: vendorId}));
    dispatch(getPerformance({vendor_id: vendorId}));
    dispatch(getWarehouse({vendor_id: vendorId}));
    dispatch(getVendorGrade({vendor_id: vendorId}));
    dispatch(getBanners({params: {vendor_id: vendorId, placement: 'homepage'}}));
  }, [dispatch, headerData, navigate, selectedVendorId]);

  // useEffect(() => {
  //   const popupPreference = localStorage.getItem('popupModal');
  //   if (popupPreference === null) {
  //     localStorage.setItem('popupModal', 'true');
  //     setShowPromotionPopup(true);
  //     return;
  //   }
  //   setShowPromotionPopup(popupPreference === 'true');
  // }, []);

  const vendorId = selectedVendorId;
  const isLoading =
    gettingHomeData ||
    isEmptyObject({
      ...(performance as object),
      ...(orderReports as object),
      ...(warehouse as object)
    });

  return (
    <PageLayout contentClassName="flex flex-col gap-4 bg-white">
      {isLoading || !vendorId ? (
        <HomeSkeleton />
      ) : (
        <>
          <BannerCarousel banners={(banners ?? []) as Banner[]} />
          {performance && (
            <>
              <section className="flex flex-col gap-4 px-3.5">
                <SectionHeader
                  title="امتیاز عملکرد"
                  description="مقایسه با ۷ روز گذشته"
                  actionLabel="جزئیات"
                  onAction={() => navigate('/vendor-score')}
                />
                {vendorGrade?.grade && (
                  <VendorBadge
                    badge={vendorGrade.grade}
                    link="https://snappmarket.landin.ir/academy/"
                  />
                )}
                <div className="flex gap-2.5">
                  <StatCard
                    data={{
                      title: 'امتیاز شما',
                      value: `${performance.score?.avg ?? 0}/100`,
                      change: `${performance.score?.percentage ?? 0}%`,
                      trend: performance.score?.trend
                    }}
                  />
                  <StatCard
                    data={{
                      title: 'رتبه در لیست فروشندگان',
                      value: performance.rank?.avg ?? 0,
                      change: performance.rank?.percentage ?? 0,
                      trend: performance.rank?.trend
                    }}
                  />
                </div>
                <ActionRow
                  navigationUrl="/vendor-score"
                  icon={<BulbOutlined className="text-xl text-warning-heavy" aria-hidden />}
                  title="راهکارهای افزایش امتیاز"
                  badge={performance.message_count}
                />
                <DashboardChart variant="performance" data={performance as PerformanceData} />
              </section>
            </>
          )}
          {orderCount && (
            <>
              <Divider className="bg-gray-02" size={2} />
              <section className="flex flex-col gap-4 px-3.5">
                <SectionHeader
                  title="فروش"
                  actionLabel="جزئیات"
                  onAction={() => navigate('/orders')}
                />
                <div className="flex gap-2.5">
                  <StatCard
                    data={{
                      title: 'فروش امروز',
                      value: `${numberFormat(orderReports?.sum_of_orders || 0)} تومان`
                    }}
                  />
                  <StatCard
                    data={{
                      title: 'سفارش‌های امروز',
                      value: orderReports?.count_of_orders || 0
                    }}
                  />
                </div>
                <DashboardChart variant="orders" data={orderCount as OrderCountData} />
              </section>
            </>
          )}
          {warehouse && (
            <>
              <Divider className="bg-gray-02" size={2} />
              <WarehouseSection data={warehouse as WarehouseData} vendorId={vendorId} />
            </>
          )}
        </>
      )}
      {/* <PromotionPopup show={showPromotionPopup} onClose={() => setShowPromotionPopup(false)} /> */}
    </PageLayout>
  );
};

export default Home;
